import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAppSettings, updateAppSettings, getUsers } from "@/lib/api";
import { notifySettingsChanged } from "@/lib/settings";

const DEFAULT_SHOW_SCORE = true;
const DEFAULT_SHOW_CURSORS = true;
const DEFAULT_SHOW_IA_SEARCH = true;
const DEFAULT_BACKGROUND_DOT_SIZE = 2;
const DEFAULT_BACKGROUND_GRID_SIZE = 24;
const DEFAULT_BACKGROUND_DOT_OPACITY = 0.12;
const DEFAULT_BACKGROUND_DOT_COLOR = "#000000";
const DEFAULT_BODY_BACKGROUND_COLOR = "#ffffff";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
function indexToLabel(i) {
  if (i < 26) return LETTERS[i];
  return LETTERS[Math.floor(i / 26) - 1] + LETTERS[i % 26];
}

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const [showScore, setShowScoreState] = useState(DEFAULT_SHOW_SCORE);
  const [showCursors, setShowCursorsState] = useState(DEFAULT_SHOW_CURSORS);
  const [showIaSearch, setShowIaSearchState] = useState(DEFAULT_SHOW_IA_SEARCH);
  const [backgroundDotSize, setBackgroundDotSizeState] = useState(DEFAULT_BACKGROUND_DOT_SIZE);
  const [backgroundGridSize, setBackgroundGridSizeState] = useState(DEFAULT_BACKGROUND_GRID_SIZE);
  const [backgroundDotOpacity, setBackgroundDotOpacityState] = useState(DEFAULT_BACKGROUND_DOT_OPACITY);
  const [backgroundDotColor, setBackgroundDotColorState] = useState(DEFAULT_BACKGROUND_DOT_COLOR);
  const [bodyBackgroundColor, setBodyBackgroundColorState] = useState(DEFAULT_BODY_BACKGROUND_COLOR);
  const [demoHideUserNames, setDemoHideUserNamesState] = useState(false);
  const [demoMaskEmail, setDemoMaskEmailState] = useState("off");
  const [demoMaskTelefono, setDemoMaskTelefonoState] = useState("off");
  const [demoMaskDireccion, setDemoMaskDireccionState] = useState("off");
  const [demoMaskPortfolio, setDemoMaskPortfolioState] = useState("off");
  const [demoMaskNombre, setDemoMaskNombreState] = useState("off");
  const [demoHideCvPreview, setDemoHideCvPreviewState] = useState(false);
  const [userDisplayNameMap, setUserDisplayNameMap] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!user) {
      setShowScoreState(DEFAULT_SHOW_SCORE);
      setShowCursorsState(DEFAULT_SHOW_CURSORS);
      setShowIaSearchState(DEFAULT_SHOW_IA_SEARCH);
      setBackgroundDotSizeState(DEFAULT_BACKGROUND_DOT_SIZE);
      setBackgroundGridSizeState(DEFAULT_BACKGROUND_GRID_SIZE);
      setBackgroundDotOpacityState(DEFAULT_BACKGROUND_DOT_OPACITY);
      setBackgroundDotColorState(DEFAULT_BACKGROUND_DOT_COLOR);
      setBodyBackgroundColorState(DEFAULT_BODY_BACKGROUND_COLOR);
      setDemoHideUserNamesState(false);
      setDemoMaskEmailState("off");
      setDemoMaskTelefonoState("off");
      setDemoMaskDireccionState("off");
      setDemoMaskPortfolioState("off");
      setDemoMaskNombreState("off");
      setDemoHideCvPreviewState(false);
      setUserDisplayNameMap({});
      return;
    }
    try {
      const data = await getAppSettings();
      setShowScoreState(data.show_score ?? DEFAULT_SHOW_SCORE);
      setShowCursorsState(data.show_cursors ?? DEFAULT_SHOW_CURSORS);
      setShowIaSearchState(data.show_ia_search ?? DEFAULT_SHOW_IA_SEARCH);
      setBackgroundDotSizeState(data.background_dot_size ?? DEFAULT_BACKGROUND_DOT_SIZE);
      setBackgroundGridSizeState(data.background_grid_size ?? DEFAULT_BACKGROUND_GRID_SIZE);
      setBackgroundDotOpacityState(data.background_dot_opacity ?? DEFAULT_BACKGROUND_DOT_OPACITY);
      setBackgroundDotColorState(data.background_dot_color ?? DEFAULT_BACKGROUND_DOT_COLOR);
      setBodyBackgroundColorState(data.body_background_color ?? DEFAULT_BODY_BACKGROUND_COLOR);
      setDemoHideUserNamesState(data.demo_hide_user_names ?? false);
      setDemoMaskEmailState(data.demo_mask_email === "fake" || data.demo_mask_email === "blur" ? data.demo_mask_email : "off");
      setDemoMaskTelefonoState(data.demo_mask_telefono === "fake" || data.demo_mask_telefono === "blur" ? data.demo_mask_telefono : "off");
      setDemoMaskDireccionState(data.demo_mask_direccion === "fake" || data.demo_mask_direccion === "blur" ? data.demo_mask_direccion : "off");
      setDemoMaskPortfolioState(data.demo_mask_portfolio === "fake" || data.demo_mask_portfolio === "blur" ? data.demo_mask_portfolio : "off");
      setDemoMaskNombreState(data.demo_mask_nombre === "fake" || data.demo_mask_nombre === "blur" ? data.demo_mask_nombre : "off");
      setDemoHideCvPreviewState(data.demo_hide_cv_preview ?? false);
    } catch (_) {
      setShowScoreState(DEFAULT_SHOW_SCORE);
      setShowCursorsState(DEFAULT_SHOW_CURSORS);
      setShowIaSearchState(DEFAULT_SHOW_IA_SEARCH);
      setBackgroundDotSizeState(DEFAULT_BACKGROUND_DOT_SIZE);
      setBackgroundGridSizeState(DEFAULT_BACKGROUND_GRID_SIZE);
      setBackgroundDotOpacityState(DEFAULT_BACKGROUND_DOT_OPACITY);
      setBackgroundDotColorState(DEFAULT_BACKGROUND_DOT_COLOR);
      setBodyBackgroundColorState(DEFAULT_BODY_BACKGROUND_COLOR);
      setDemoHideUserNamesState(false);
      setDemoMaskEmailState("off");
      setDemoMaskTelefonoState("off");
      setDemoMaskDireccionState("off");
      setDemoMaskPortfolioState("off");
      setDemoMaskNombreState("off");
      setDemoHideCvPreviewState(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user || !demoHideUserNames) {
      setUserDisplayNameMap({});
      return;
    }
    let cancelled = false;
    getUsers()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data.users) ? data.users : [];
        const sorted = [...list].sort((a, b) => (a.email || "").localeCompare(b.email || ""));
        const map = {};
        sorted.forEach((u, i) => {
          const email = (u.email || "").trim().toLowerCase();
          if (email) map[email] = `Usuario ${indexToLabel(i)}`;
        });
        setUserDisplayNameMap(map);
      })
      .catch(() => setUserDisplayNameMap({}));
    return () => { cancelled = true; };
  }, [user, demoHideUserNames]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (!user) return;
    const onFocus = () => fetchSettings();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, fetchSettings]);

  const setShowScore = useCallback(async (checked) => {
    setShowScoreState(checked);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ show_score: checked });
    } catch (_) {
      setShowScoreState((prev) => !prev);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setShowCursors = useCallback(async (checked) => {
    setShowCursorsState(checked);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ show_cursors: checked });
      notifySettingsChanged();
    } catch (_) {
      setShowCursorsState((prev) => !prev);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setShowIaSearch = useCallback(async (checked) => {
    setShowIaSearchState(checked);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ show_ia_search: checked });
    } catch (_) {
      setShowIaSearchState((prev) => !prev);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updateBackgroundSettings = useCallback(
    async (dotSize, gridSize, dotOpacity, dotColor, bgColor) => {
      if (!user) return;
      setLoading(true);
      try {
        await updateAppSettings({
          background_dot_size: dotSize,
          background_grid_size: gridSize,
          background_dot_opacity: dotOpacity,
          background_dot_color: dotColor || DEFAULT_BACKGROUND_DOT_COLOR,
          body_background_color: bgColor || DEFAULT_BODY_BACKGROUND_COLOR,
        });
        setBackgroundDotSizeState(dotSize);
        setBackgroundGridSizeState(gridSize);
        setBackgroundDotOpacityState(dotOpacity);
        setBackgroundDotColorState(dotColor || DEFAULT_BACKGROUND_DOT_COLOR);
        setBodyBackgroundColorState(bgColor || DEFAULT_BODY_BACKGROUND_COLOR);
      } catch (_) {
        throw _;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const setDemoHideUserNames = useCallback(async (checked) => {
    setDemoHideUserNamesState(checked);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_hide_user_names: checked });
    } catch (_) {
      setDemoHideUserNamesState((prev) => !prev);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setDemoMaskEmail = useCallback(async (value) => {
    setDemoMaskEmailState(value);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_mask_email: value });
    } catch (_) {
      setDemoMaskEmailState("off");
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setDemoMaskTelefono = useCallback(async (value) => {
    setDemoMaskTelefonoState(value);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_mask_telefono: value });
    } catch (_) {
      setDemoMaskTelefonoState("off");
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setDemoMaskDireccion = useCallback(async (value) => {
    setDemoMaskDireccionState(value);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_mask_direccion: value });
    } catch (_) {
      setDemoMaskDireccionState("off");
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setDemoMaskPortfolio = useCallback(async (value) => {
    setDemoMaskPortfolioState(value);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_mask_portfolio: value });
    } catch (_) {
      setDemoMaskPortfolioState("off");
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setDemoMaskNombre = useCallback(async (value) => {
    setDemoMaskNombreState(value);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_mask_nombre: value });
    } catch (_) {
      setDemoMaskNombreState("off");
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setDemoHideCvPreview = useCallback(async (checked) => {
    setDemoHideCvPreviewState(checked);
    if (!user) return;
    setLoading(true);
    try {
      await updateAppSettings({ demo_hide_cv_preview: checked });
    } catch (_) {
      setDemoHideCvPreviewState((prev) => !prev);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getDisplayName = useCallback((emailOrUser) => {
    if (!demoHideUserNames) return null;
    const email = typeof emailOrUser === "string" ? emailOrUser : (emailOrUser?.email || "").trim().toLowerCase();
    return userDisplayNameMap[email] || "Usuario";
  }, [demoHideUserNames, userDisplayNameMap]);

  const maskValue = useCallback((value, type) => {
    if (value == null || value === "") return value;
    const str = String(value).trim();
    if (!str) return value;
    let mode = "off";
    if (type === "email") mode = demoMaskEmail;
    else if (type === "telefono") mode = demoMaskTelefono;
    else if (type === "direccion") mode = demoMaskDireccion;
    else if (type === "portfolio") mode = demoMaskPortfolio;
    else if (type === "nombre") mode = demoMaskNombre;
    if (mode === "off") return str;
    if (mode === "fake") {
      if (type === "email") return "ejemplo@ejemplo.com";
      if (type === "telefono") return "+34 600 000 000";
      if (type === "direccion") return "Ciudad, Provincia";
      if (type === "portfolio") return "https://ejemplo.com";
      if (type === "nombre") return "Nombre Apellido";
      return "•••";
    }
    if (mode === "blur") return "••••••••";
    return str;
  }, [demoMaskEmail, demoMaskTelefono, demoMaskDireccion, demoMaskPortfolio, demoMaskNombre]);

  const value = {
    showScore,
    showCursors,
    showIaSearch,
    setShowScore,
    setShowCursors,
    setShowIaSearch,
    backgroundDotSize,
    backgroundGridSize,
    backgroundDotOpacity,
    backgroundDotColor,
    bodyBackgroundColor,
    setBackgroundDotSizeState,
    setBackgroundGridSizeState,
    setBackgroundDotOpacityState,
    setBackgroundDotColorState,
    setBodyBackgroundColorState,
    updateBackgroundSettings,
    loading,
    refetchSettings: fetchSettings,
    demoHideUserNames,
    demoMaskEmail,
    demoMaskTelefono,
    demoMaskDireccion,
    demoMaskPortfolio,
    demoMaskNombre,
    demoHideCvPreview,
    setDemoHideUserNames,
    setDemoMaskEmail,
    setDemoMaskTelefono,
    setDemoMaskDireccion,
    setDemoMaskPortfolio,
    setDemoMaskNombre,
    setDemoHideCvPreview,
    getDisplayName,
    maskValue,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    return {
      showScore: DEFAULT_SHOW_SCORE,
      showCursors: DEFAULT_SHOW_CURSORS,
      showIaSearch: DEFAULT_SHOW_IA_SEARCH,
      setShowScore: () => {},
      setShowCursors: () => {},
      setShowIaSearch: () => {},
      backgroundDotSize: DEFAULT_BACKGROUND_DOT_SIZE,
      backgroundGridSize: DEFAULT_BACKGROUND_GRID_SIZE,
      backgroundDotOpacity: DEFAULT_BACKGROUND_DOT_OPACITY,
      backgroundDotColor: DEFAULT_BACKGROUND_DOT_COLOR,
      bodyBackgroundColor: DEFAULT_BODY_BACKGROUND_COLOR,
      setBackgroundDotSizeState: () => {},
      setBackgroundGridSizeState: () => {},
      setBackgroundDotOpacityState: () => {},
      setBackgroundDotColorState: () => {},
      setBodyBackgroundColorState: () => {},
      updateBackgroundSettings: async () => {},
      loading: false,
      refetchSettings: () => {},
      demoHideUserNames: false,
      demoMaskEmail: "off",
      demoMaskTelefono: "off",
      demoMaskDireccion: "off",
      demoMaskPortfolio: "off",
      demoMaskNombre: "off",
      demoHideCvPreview: false,
      setDemoHideUserNames: () => {},
      setDemoMaskEmail: () => {},
      setDemoMaskTelefono: () => {},
      setDemoMaskDireccion: () => {},
      setDemoMaskPortfolio: () => {},
      setDemoMaskNombre: () => {},
      setDemoHideCvPreview: () => {},
      getDisplayName: () => null,
      maskValue: (v) => v,
    };
  }
  return ctx;
}
